import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { value: 'CocoaLife', label: 'CocoaLife™ Products' },
  { value: 'Merch', label: 'Merch & Apparel' },
  { value: 'Book Orders', label: 'Book Orders (Oumaumau)' },
  { value: 'Educators Services', label: 'Educators / Clear Lesson Studio' },
  { value: 'Dark Pan', label: 'The Dark Pan Project' },
  { value: 'General Inquiry', label: 'General Inquiry' },
];

export default function ContactWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'General Inquiry',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', service: 'General Inquiry', message: '' });
      setShowForm(false);
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openMessenger = () => {
    window.open('https://m.me/GradFawkes', '_blank');
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 lg:bottom-8 right-4 lg:right-8 z-50 w-14 h-14 rounded-full bg-secondary text-white shadow-lg flex items-center justify-center"
        style={{ boxShadow: '0 4px 20px rgba(217, 84, 12, 0.4)' }}
        data-testid="contact-widget-btn"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      {/* Chat Options */}
      <AnimatePresence>
        {isOpen && !showForm && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-40 lg:bottom-24 right-4 lg:right-8 z-50 w-80 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
            data-testid="contact-options"
          >
            <div className="p-4 bg-secondary text-white">
              <h3 className="font-bold text-lg">How can we help?</h3>
              <p className="text-sm text-white/80">Choose how you'd like to connect</p>
            </div>
            
            <div className="p-4 space-y-3">
              {/* Messenger Option */}
              <button
                onClick={openMessenger}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#0084FF] hover:bg-[#0084FF]/90 transition-colors text-white"
                data-testid="messenger-btn"
              >
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.14.26.34.29.55l.06 1.72c.02.55.56.91 1.07.72l1.93-.77c.17-.07.36-.08.53-.03.9.24 1.87.37 2.98.37 5.64 0 10-4.13 10-9.7S17.64 2 12 2zm5.85 7.65l-2.84 4.51c-.45.72-1.42.89-2.08.37l-2.26-1.7c-.17-.13-.4-.13-.57 0l-3.06 2.32c-.41.31-.94-.18-.67-.62l2.84-4.51c.45-.72 1.42-.89 2.08-.37l2.26 1.7c.17.13.4.13.57 0l3.06-2.32c.41-.31.94.18.67.62z"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold">Facebook Messenger</p>
                  <p className="text-xs text-white/80">Chat with us live</p>
                </div>
              </button>

              {/* Contact Form Option */}
              <button
                onClick={() => setShowForm(true)}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
                data-testid="contact-form-btn"
              >
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Send className="w-5 h-5 text-secondary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Send a Message</p>
                  <p className="text-xs text-muted-foreground">We'll email you back</p>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Form */}
      <AnimatePresence>
        {isOpen && showForm && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-40 lg:bottom-24 right-4 lg:right-8 z-50 w-80 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden max-h-[70vh] overflow-y-auto"
            data-testid="contact-form"
          >
            <div className="p-4 bg-secondary text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Contact Us</h3>
                <p className="text-sm text-white/80">We'll get back to you soon</p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <Input
                placeholder="Your Name *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="contact-name"
              />
              <Input
                type="email"
                placeholder="Your Email *"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="contact-email"
              />
              <Input
                type="tel"
                placeholder="Phone (optional)"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="contact-phone"
              />
              <select
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="contact-service"
              >
                {serviceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Textarea
                placeholder="Your Message *"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={3}
                data-testid="contact-message"
              />
              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={loading}
                data-testid="contact-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
